import { getPublicEnv, type PublicEnvKey } from "@janeway/app-sdk/public-env";

export interface JanewayApp {
  /** Stabiler Identifier — matcht die janeway-status Service-ID (fuer Live-Status). */
  id: string;
  name: string;
  url: string;
  logo: string;
  /**
   * Optionaler lucide-Glyph-Name (PascalCase, z.B. "Building2"). Kommt aus der
   * live `infra_apps`-Registry und erlaubt neuen Apps ein Icon OHNE Code-Aenderung
   * (AppIcon loest ihn dynamisch auf). Bei den statischen Apps ungesetzt — dort
   * greift der bestehende APP_GLYPHS-Lookup ueber die id.
   */
  glyph?: string;
  /**
   * Feste Kategorie aus `infra_apps.gruppe` (M1014). Fehlt sie (Registry ohne Spalte,
   * unbekannter Schluessel), ordnet `gruppiereApps` die App unter „Werkzeuge" ein.
   */
  gruppe?: AppGruppe;
  provides?: import("./app-manifest").Capability[];
  consumes?: import("./app-manifest").Capability[];
}

/**
 * Zeile der live `infra_apps`-Registry (Supabase). Spiegelt das Schema aus
 * Migration 219 — Source of Truth fuer AppSwitcher + Hub, wenn erreichbar.
 */
export interface InfraAppRow {
  id: string;
  name: string;
  sub: string;
  glyph: string | null;
  category: string;
  sort_order: number;
  enabled: boolean;
  /** Seit M1014; fehlt in einer Registry ohne die Migration. */
  gruppe?: string | null;
  provides?: string[];
  consumes?: string[];
}

interface AppDef {
  id: string;
  name: string;
  /** Subdomain (z.B. "chat") — fuer Hostname-Ableitung, wenn keine env-URL gesetzt. */
  sub: string;
  /**
   * Optionaler Override. Es gibt NICHT fuer jede App eine `NEXT_PUBLIC_*_URL`:
   * drive, docs, sheets, slides, meet und mail haben keine (`PUBLIC_ENV_KEYS` im
   * app-sdk ist die Liste). Ohne Key faellt die Aufloesung auf
   * `sub + baseDomain` — genau der Weg, den `mapInfraAppsToJanewayApps` fuer
   * Registry-Apps ohne Override schon geht.
   */
  envKey?: PublicEnvKey;
  logo: string;
  gruppe: AppGruppe;
}

/**
 * Feste App-Kategorien in Anzeigereihenfolge (Betreiber 2026-10-12, Plan
 * 2026-10-12-dashboard-raster Phase 1b). Quelle der Zuordnung ist
 * `infra_apps.gruppe`; die Schluessel hier muessen dieselben sein wie in M1014.
 */
export const APP_GRUPPEN = [
  { schluessel: "ki", titel: "KI & Automatisierung" },
  { schluessel: "kommunikation", titel: "Kommunikation" },
  { schluessel: "organisation", titel: "Organisation" },
  { schluessel: "dateien", titel: "Dateien & Dokumente" },
  { schluessel: "business", titel: "Business" },
  { schluessel: "werkzeuge", titel: "Werkzeuge" },
  { schluessel: "system", titel: "System" },
] as const;

export type AppGruppe = (typeof APP_GRUPPEN)[number]["schluessel"];

const ERSATZ_GRUPPE: AppGruppe = "werkzeuge";
const GRUPPEN_SCHLUESSEL = new Set<string>(APP_GRUPPEN.map((g) => g.schluessel));

function alsGruppe(wert: string | null | undefined): AppGruppe | undefined {
  return wert && GRUPPEN_SCHLUESSEL.has(wert) ? (wert as AppGruppe) : undefined;
}

export interface AppGruppeMitApps {
  schluessel: AppGruppe;
  titel: string;
  apps: JanewayApp[];
}

/**
 * Teilt Apps in die festen Kategorien, in der Reihenfolge von `APP_GRUPPEN`.
 * Leere Gruppen fallen weg. Innerhalb einer Gruppe bleibt die Eingabereihenfolge
 * (die Registry liefert nach `sort_order`). Ohne oder mit unbekannter Gruppe → Werkzeuge.
 * Pur + deterministisch.
 */
export function gruppiereApps(apps: JanewayApp[]): AppGruppeMitApps[] {
  const nachGruppe = new Map<AppGruppe, JanewayApp[]>();
  for (const app of apps) {
    const g = alsGruppe(app.gruppe) ?? ERSATZ_GRUPPE;
    const liste = nachGruppe.get(g);
    if (liste) liste.push(app);
    else nachGruppe.set(g, [app]);
  }
  return APP_GRUPPEN.filter((g) => nachGruppe.has(g.schluessel)).map((g) => ({
    schluessel: g.schluessel,
    titel: g.titel,
    apps: nachGruppe.get(g.schluessel)!,
  }));
}

/**
 * Statischer Fallback fuer `useJanewayApps`, wenn die `infra_apps`-Registry nicht
 * antwortet ("GARANTIE: NIE ein Throw im Render-Pfad").
 *
 * **Er muss die Prod-Registry abbilden, nicht eine Teilmenge.** Bis 2026-09-08
 * standen hier 9 von 16 Prod-Apps; bei jedem Aussetzer der Abfrage sah der Kunde
 * still 9 statt 16 Kacheln — ohne Fehler, ohne Hinweis — und las das als „meine
 * Apps sind weg" (ARCH-16). Es fehlten mail-client, drive, docs, sheets, slides,
 * meet und messenger.
 *
 * Die Gegenprobe steht in `app-registry.prod.json` (Abzug der Prod-Registry mit
 * Stand-Datum und dem Befehl, der ihn erzeugt); `src/__tests__/apps.test.ts`
 * haelt beide Listen gegeneinander, statt die Ids ein zweites Mal aufzuschreiben.
 * Wer eine App hinzufuegt, zieht den Abzug nach und traegt sie hier ein — der
 * Test nennt dann beide Seiten.
 *
 * Kein Glyph-Feld noetig: `APP_GLYPHS` loest alle diese Ids auf.
 */
const APP_DEFS: AppDef[] = [
  { id: "janeway-ai-chat",     name: "AI Chat",     sub: "chat",        envKey: "NEXT_PUBLIC_AI_URL",        logo: "/app-logos/ai.png", gruppe: "ki" },
  { id: "janeway-automations", name: "Automations", sub: "automations", envKey: "NEXT_PUBLIC_AGENTS_URL",    logo: "/app-logos/agents.png", gruppe: "ki" },
  { id: "janeway-notes",       name: "Notes",       sub: "notes",       envKey: "NEXT_PUBLIC_NOTES_URL",     logo: "/app-logos/notes.png", gruppe: "dateien" },
  { id: "janeway-contacts",    name: "Contacts",    sub: "contacts",    envKey: "NEXT_PUBLIC_CONTACTS_URL",  logo: "/app-logos/contacts.png", gruppe: "organisation" },
  { id: "janeway-calendar",    name: "Calendar",    sub: "calendar",    envKey: "NEXT_PUBLIC_CALENDAR_URL",  logo: "/app-logos/calendar.png", gruppe: "organisation" },
  { id: "janeway-tasks",       name: "Tasks",       sub: "tasks",       envKey: "NEXT_PUBLIC_TASKS_URL",     logo: "/app-logos/tasks.png", gruppe: "organisation" },
  { id: "janeway-immo",        name: "Immobilien",  sub: "immo",        envKey: "NEXT_PUBLIC_IMMO_URL",      logo: "/app-logos/immo.png", gruppe: "business" },
  { id: "janeway-mail-client", name: "Mail",        sub: "mail",                                             logo: "/app-logos/mail.png", gruppe: "kommunikation" },
  { id: "janeway-drive",       name: "Drive",       sub: "drive",                                            logo: "/app-logos/drive.png", gruppe: "dateien" },
  { id: "janeway-docs",        name: "Docs",        sub: "docs",                                             logo: "/app-logos/docs.png", gruppe: "dateien" },
  { id: "janeway-sheets",      name: "Sheets",      sub: "sheets",                                           logo: "/app-logos/sheets.png", gruppe: "dateien" },
  { id: "janeway-slides",      name: "Slides",      sub: "slides",                                           logo: "/app-logos/slides.png", gruppe: "dateien" },
  { id: "janeway-meet",        name: "Meet",        sub: "meet",                                             logo: "/app-logos/meet.png", gruppe: "kommunikation" },
  { id: "janeway-messenger",   name: "Messenger",   sub: "messenger",   envKey: "NEXT_PUBLIC_MESSENGER_URL", logo: "/app-logos/messenger.png", gruppe: "kommunikation" },
  { id: "janeway-affiliate",   name: "Affiliate",   sub: "affiliate",   envKey: "NEXT_PUBLIC_AFFILIATE_URL", logo: "/app-logos/affiliate.png", gruppe: "business" },
  { id: "janeway-api",         name: "API",         sub: "api",         envKey: "NEXT_PUBLIC_API_URL",       logo: "/app-logos/api.png", gruppe: "werkzeuge" },
  // janeway-weather ABSICHTLICH NICHT hier: dieses Array bildet die PROD-Registry ab
  // (siehe Kommentar oben + app-registry.prod.json._hinweis), Weather ist Stand
  // 2026-09-27 Labs-only ohne Prod-Container (PROD-FREIGABE.md, setup.yaml
  // `weather: false`) — derselbe Ausschluss wie bei brain/cashflow/crm/hosting/
  // ai-studio. Sichtbar wird die App ueber die LIVE `infra_apps`-Registry
  // (sync-app-registry.ts), nicht ueber diesen statischen Fallback. Ein Eintrag
  // hier liesse apps.test.ts rot laufen ("erfindet keine App, die es in Prod
  // nicht gibt") — nachtragen, sobald Weather nach Prod geht und der
  // Prod-Abzug neu gezogen ist.
];

/**
 * Subdomain -> Public-Env-Key. Pro Install kann eine explizite `NEXT_PUBLIC_*_URL`
 * gesetzt sein (hat Vorrang vor der baseDomain-Ableitung). Deckt die bekannten
 * Subs ab; unbekannte (neue Apps aus der DB) haben keinen Override und fallen
 * sauber auf `sub.baseDomain` zurueck.
 */
const SUB_ENV_KEYS: Record<string, PublicEnvKey> = {
  chat: "NEXT_PUBLIC_AI_URL",
  automations: "NEXT_PUBLIC_AGENTS_URL",
  notes: "NEXT_PUBLIC_NOTES_URL",
  contacts: "NEXT_PUBLIC_CONTACTS_URL",
  calendar: "NEXT_PUBLIC_CALENDAR_URL",
  tasks: "NEXT_PUBLIC_TASKS_URL",
  immo: "NEXT_PUBLIC_IMMO_URL",
  affiliate: "NEXT_PUBLIC_AFFILIATE_URL",
  api: "NEXT_PUBLIC_API_URL",
  admin: "NEXT_PUBLIC_ADMIN_URL",
  status: "NEXT_PUBLIC_STATUS_URL",
  auth: "NEXT_PUBLIC_AUTH_URL",
  hub: "NEXT_PUBLIC_HUB_URL",
  messenger: "NEXT_PUBLIC_MESSENGER_URL",
};

/**
 * Loest eine Cross-App-URL auf. Reihenfolge:
 *   1. env (`NEXT_PUBLIC_*_URL`, pro Install gesetzt) — hat Vorrang
 *   2. aus `baseDomain` abgeleitet (`https://<sub>.<baseDomain>`) — labs zeigt auf labs
 *   3. prod-Fallback (`https://<sub>.janeway.io`)
 * `baseDomain` kommt vom Server (Host-Header) -> SSR und Client liefern identische
 * Hrefs (keine Hydration-Mismatch) und die richtigen Links stehen schon im SSR-HTML.
 */
/**
 * Apps, die mit Basispfad `/<sub>` gebaut sind und deshalb AUCH unter
 * `app.<domain>/<sub>` laufen (Spec 2026-10-11-app-suite-dashboard, Teil B).
 * Ein Eintrag hier ohne Basispfad-Bau fuehrt ins 404 — erst bauen, dann eintragen.
 */
// PAUSIERT seit 2026-10-11 (Betreiber): ein Bau mit Basispfad zeigte auf der Subdomain
// den Pfad doppelt (`tasks.<domain>/tasks/today`), zwei Bauten je App waren zu teuer.
// Leer = jede App oeffnet auf ihrer Subdomain. Die Mechanik (Basispfad-Bau, fetch-Praefix,
// Weiche im Hub) bleibt und wirkt erst wieder mit Eintraegen hier.
export const APPS_UNTER_APP_DOMAIN: ReadonlySet<string> = new Set<string>([]);

/** Laeuft die Seite unter `app.<domain>`, der Suite als einer App? */
export function istAppHost(hostname: string | null | undefined): boolean {
  return !!hostname && hostname.split(":")[0].split(".")[0] === "app" && hostname.split(".").length >= 3;
}

function imBrowserAppHost(): boolean {
  return typeof window !== "undefined" && istAppHost(window.location.hostname);
}

/**
 * Adresse einer App. Unter `app.<domain>` die Pfad-Instanz (bleibt in der Domain,
 * die installierte PWA verliert sonst ihren Rahmen), sonst env → Subdomain.
 */
export function janewayUrl(
  sub: string,
  envKey?: PublicEnvKey,
  baseDomain?: string | null,
  appHost: boolean = imBrowserAppHost(),
): string {
  const basis = baseDomain || (typeof window !== "undefined" ? deriveBaseDomain(window.location.hostname) : null);
  if (appHost && basis && APPS_UNTER_APP_DOMAIN.has(sub)) return `https://app.${basis}/${sub}`;
  return (envKey && getPublicEnv(envKey)) || `https://${sub}.${baseDomain || "janeway.io"}`;
}

/**
 * Kanonische App-Registry — Source of Truth fuer AppSwitcher (TopNav) UND
 * HubLauncher. `baseDomain` optional: ohne -> env/prod (AppSwitcher-Default),
 * mit -> Hostname-Ableitung (Hub, server-seitig befuellt).
 */
export function getJanewayApps(baseDomain?: string | null): JanewayApp[] {
  return APP_DEFS.map((d) => ({ id: d.id, name: d.name, url: janewayUrl(d.sub, d.envKey, baseDomain), logo: d.logo, gruppe: d.gruppe }));
}


/**
 * Weitere Dienste (Affiliate, API, Admin, Status) — hinter „Mehr" ausklappbar
 * (AppSwitcher UND Hub-Grid), getrennt von den Enduser-Suite-Apps. Admin ist
 * bewusst fuer alle sichtbar: fehlt die Berechtigung, faengt die Ziel-App das ab
 * (Hinweis + Login). Logos im Stil der uebrigen app-logos.
 */
export function getSystemApps(baseDomain?: string | null): JanewayApp[] {
  return [
    // affiliate + api sind jetzt Default-Apps (APP_DEFS/'main', siehe Migration 230) —
    // hier bleiben nur admin + status hinter "Mehr".
    { id: "janeway-admin", name: "Admin", url: janewayUrl("admin", "NEXT_PUBLIC_ADMIN_URL", baseDomain), logo: "/app-logos/admin.png", gruppe: "system" },
    { id: "janeway-status", name: "Status", url: janewayUrl("status", "NEXT_PUBLIC_STATUS_URL", baseDomain), logo: "/app-logos/status.png", gruppe: "system" },
  ];
}

/**
 * Mappt die live `infra_apps`-Registry auf `JanewayApp[]`, getrennt nach
 * `main` (Suite-Apps) und `system` (weitere Dienste). URL wird wie bei den
 * statischen Apps aufgeloest (env-Override -> sub+baseDomain -> prod-Fallback);
 * der Glyph-Name wird durchgereicht, sodass neue Apps OHNE Code-Aenderung ein
 * Icon bekommen. Das `logo`-Feld bleibt aus Kompat-Gruenden gesetzt (Initialen-
 * Fallback im logo-Modus), getragen wird das Icon aber ueber den Glyph.
 *
 * Pur + deterministisch (keine I/O) — der Hook ruft das mit den DB-Zeilen auf.
 */
export function mapInfraAppsToJanewayApps(
  rows: InfraAppRow[],
  baseDomain?: string | null,
  appHost: boolean = imBrowserAppHost(),
): { main: JanewayApp[]; system: JanewayApp[] } {
  const toApp = (r: InfraAppRow): JanewayApp => {
    return {
      id: r.id,
      name: r.name,
      url: janewayUrl(r.sub, SUB_ENV_KEYS[r.sub], baseDomain, appHost),
      logo: `/app-logos/${r.sub}.png`,
      glyph: r.glyph ?? undefined,
      gruppe: alsGruppe(r.gruppe) ?? (r.category === "system" ? "system" : undefined),
      provides: (r.provides ?? []) as import("./app-manifest").Capability[],
      consumes: (r.consumes ?? []) as import("./app-manifest").Capability[],
    };
  };
  const sorted = [...rows].sort((a, b) => a.sort_order - b.sort_order);
  return {
    system: sorted.filter((r) => r.category === "system").map(toApp),
    // alles ausser "system" ist Suite-App (unbekannte category faellt auf main).
    main: sorted.filter((r) => r.category !== "system").map(toApp),
  };
}

/**
 * Leitet die Hub-URL aus dem aktuellen Hostname ab: `chat.janewaylabs.com` ->
 * `https://hub.janewaylabs.com`, `tools.janeway.io` -> `https://hub.janeway.io`.
 * Liefert `null` bei localhost / IP / Apex (<3 Labels) — dann greift env oder der
 * `"/"`-Fallback. Spiegelt `auth/lib/auth-helpers.deriveHubUrl`, lebt aber hier,
 * weil der AppSwitcher (gui-elements) keinen Zugriff auf die auth-App hat.
 *
 * WARUM: `NEXT_PUBLIC_HUB_URL` ist nicht in jeder Install gesetzt; ohne diese
 * Ableitung fiel die "Alle Apps"-Aktion auf `"/"` (= aktuelle App) zurueck.
 */
export function deriveHubUrlFromHost(hostname: string | null | undefined): string | null {
  if (!hostname) return null;
  const host = hostname.split(":")[0];
  if (/^[\d.]+$/.test(host)) return null; // IP
  const parts = host.split(".");
  if (parts.length < 3) return null; // localhost / Apex
  parts[0] = "hub";
  return `https://${parts.join(".")}`;
}

/**
 * Leitet die Basis-Domain aus dem Hostname ab: `immo.janewaylabs.com` ->
 * `janewaylabs.com`, `contacts.janeway.io` -> `janeway.io`. `null` bei
 * localhost / IP / Apex (<3 Labels). So zeigt der URL-Fallback fuer Registry-Apps
 * OHNE `NEXT_PUBLIC_*_URL`-Override (z.B. immo) auf die AKTUELLE Umgebung statt
 * hartkodiert auf `janeway.io`.
 */
export function deriveBaseDomain(hostname: string | null | undefined): string | null {
  if (!hostname) return null;
  const host = hostname.split(":")[0];
  if (/^[\d.]+$/.test(host)) return null; // IP
  const parts = host.split(".");
  if (parts.length < 3) return null; // localhost / Apex
  return parts.slice(1).join("."); // erstes Label (Subdomain) abschneiden
}

/**
 * Kanonische Hub-URL fuer "Alle Apps": env hat Vorrang, sonst aus dem aktuellen
 * Hostname abgeleitet, sonst `"/"`. `baseDomain` erlaubt server-seitige Aufloesung.
 */
export function janewayHubUrl(baseDomain?: string | null): string {
  // Unter app.<domain> IST die Startseite die Wurzel derselben Domain.
  if (imBrowserAppHost()) return `${window.location.origin}/`;
  const env = getPublicEnv("NEXT_PUBLIC_HUB_URL");
  if (env) return env;
  if (baseDomain) return `https://hub.${baseDomain}`;
  if (typeof window !== "undefined") {
    const derived = deriveHubUrlFromHost(window.location.hostname);
    if (derived) return derived;
  }
  return "/";
}

/**
 * Geordnete Favoriten-Aufteilung — geteilt von AppSwitcher (TopNav) UND Hub-Grid,
 * damit beide dieselbe „direkt sichtbar vs. hinter ‚Mehr'"-Logik nutzen:
 *   - `direct`: die Favoriten in `favoriteIds`-Reihenfolge (NICHT Katalog-Reihen-
 *     folge), nur existierende & nicht-ausgeblendete Apps. Unbekannte ids (App aus
 *     der Registry verschwunden) werden uebersprungen.
 *   - `more`: der Rest (nicht-Favorit, nicht-ausgeblendet) in `allApps`-Reihenfolge.
 *   - ausgeblendete (`hiddenIds`) tauchen in KEINER der beiden Listen auf.
 *
 * Pur + deterministisch (keine I/O). `hiddenIds` hat Vorrang vor `favoriteIds`
 * (eine App, die zugleich Favorit und ausgeblendet ist, bleibt ausgeblendet).
 *
 * `autoDirectIds` = ids, die auch OHNE Eintrag in `favoriteIds` nach `direct`
 * gehoeren. Hintergrund: `favoriteIds`/`hiddenIds` speichern nicht, welche Apps
 * der Nutzer je gesehen hat — „neu registriert" und „bewusst entsternt" sehen im
 * Datenmodell identisch aus (beides weder Favorit noch ausgeblendet). Ohne diesen
 * Parameter fiel jede nach dem letzten Speichern hinzugekommene App nach `more`
 * und verschwand hinter dem „Mehr"-Toggle des AppSwitchers. Der Aufrufer loest die
 * Mehrdeutigkeit ueber `user_preferences.hub_known_ids` auf und reicht
 * `mainIds \ knownIds` durch. Ausgeblendet schlaegt weiterhin alles.
 */
export function splitByFavorites(
  allApps: JanewayApp[],
  favoriteIds: string[],
  hiddenIds: string[],
  autoDirectIds: string[] = [],
): { direct: JanewayApp[]; more: JanewayApp[] } {
  const hiddenSet = new Set(hiddenIds);
  const byId = new Map(allApps.map((a) => [a.id, a]));

  const direct: JanewayApp[] = [];
  const seen = new Set<string>();
  for (const id of favoriteIds) {
    if (seen.has(id) || hiddenSet.has(id)) continue; // Duplikat / ausgeblendet
    const app = byId.get(id);
    if (!app) continue; // unbekannte id (App nicht mehr in der Registry)
    seen.add(id);
    direct.push(app);
  }

  // Neu hinzugekommene Apps ans ENDE von direct — die Nutzer-Sortierung der
  // echten Favoriten bleibt unberuehrt. Iteration ueber `allApps` (statt ueber
  // autoDirectIds), damit die Reihenfolge der Registry folgt und unbekannte ids
  // von selbst herausfallen.
  const autoSet = new Set(autoDirectIds);
  for (const app of allApps) {
    if (!autoSet.has(app.id) || seen.has(app.id) || hiddenSet.has(app.id)) continue;
    seen.add(app.id);
    direct.push(app);
  }

  const more = allApps.filter((a) => !seen.has(a.id) && !hiddenSet.has(a.id));
  return { direct, more };
}

/**
 * Verschiebt eine Favoriten-id im geordneten `favoriteIds`-Array um `delta`
 * Positionen (−1 = vor, +1 = zurueck) — fuer die Reorder-UI im Hub-Edit-Modus.
 * Liefert IMMER ein neues Array (kein In-place-Mutate). Out-of-bounds (am Anfang
 * weiter nach vorne / am Ende weiter nach hinten) ist ein No-Op und gibt die
 * Liste unveraendert zurueck; unbekannte id ebenso. Pur + deterministisch.
 */
export function moveFavorite(favoriteIds: string[], id: string, delta: number): string[] {
  const from = favoriteIds.indexOf(id);
  if (from === -1) return favoriteIds;
  const to = from + delta;
  if (to < 0 || to >= favoriteIds.length) return favoriteIds;
  const next = [...favoriteIds];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

/** Case-insensitive Filter nach App-Name (fuer den Type-to-filter-Launcher). */
export function filterApps(apps: JanewayApp[], query: string): JanewayApp[] {
  const q = query.trim().toLowerCase();
  if (!q) return apps;
  return apps.filter((a) => a.name.toLowerCase().includes(q));
}
